import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, ChevronLeft } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

// Each answered option gives +1 to the result it points at. The winner is the
// result with the most points; ties go to whichever result is listed first.
export const computePersonalityResult = (questions, results, answers) => {
  if (!results.length) return null;
  const score = {};
  results.forEach((r) => { score[r.id] = 0; });
  questions.forEach((q) => {
    const opt = (q.options || []).find((o) => o.id === answers[q.id]);
    if (opt && score[opt.resultId] !== undefined) score[opt.resultId] += 1;
  });
  return results.reduce((best, r) => (score[r.id] > score[best.id] ? r : best), results[0]);
};

const PersonalityQuizElement = ({
  element,
  isEditor = false,
  disabled = false,
  slideScale = 1,
}) => {
  const { t } = useTranslation();
  const { content } = element;
  const {
    title = '',
    questions = [],
    results = [],
    accentColor = '#2A4D25',
    optionBgColor = '#ffffff',
    textColor = '#1f2937',
    fontFamily = 'Evolventa',
    fontSize = 18,
    previewResultId = null,
  } = content || {};

  const playableQuestions = useMemo(
    () => questions.filter((q) => (q.options || []).some((o) => (o.text && o.text.trim()) || o.imageUrl)),
    [questions],
  );

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [pickedId, setPickedId] = useState(null);
  const timeoutRef = useRef(null);
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Editor edits can remove questions — restart so we never point past the end.
  useEffect(() => {
    setStep(0);
    setAnswers({});
    setPickedId(null);
  }, [playableQuestions.length, results.length]);

  const interactive = !isEditor && !disabled;
  const finished = playableQuestions.length > 0 && step >= playableQuestions.length;
  // In the editor the teacher can pin one result to see how its screen looks.
  const editorPreviewResult = isEditor ? results.find((r) => r.id === previewResultId) : null;
  const result = useMemo(
    () => editorPreviewResult
      || (finished ? computePersonalityResult(playableQuestions, results, answers) : null),
    [editorPreviewResult, finished, playableQuestions, results, answers],
  );

  const s = typeof slideScale === 'number' && slideScale > 0 ? slideScale : 1;
  const fs = Math.max(8, fontSize * s);
  const pad = Math.max(6, Math.round(16 * s));
  const gap = Math.max(4, Math.round(10 * s));

  const choose = (question, option) => {
    if (!interactive || pickedId) return;
    setPickedId(option.id);
    setAnswers((a) => ({ ...a, [question.id]: option.id }));
    timeoutRef.current = setTimeout(() => {
      setPickedId(null);
      setStep((n) => n + 1);
    }, 350);
  };

  const goBack = () => {
    if (!interactive || pickedId || step === 0) return;
    setStep((n) => n - 1);
  };

  const restart = () => {
    clearTimeout(timeoutRef.current);
    setPickedId(null);
    setAnswers({});
    setStep(0);
  };

  if (playableQuestions.length === 0 || results.length === 0) {
    return (
      <div
        className="w-full h-full rounded-xl flex items-center justify-center text-center text-sm text-gray-400 p-4"
        style={{ backgroundColor: '#ffffff', border: '1px dashed #e5e7eb' }}
      >
        {t('editor.configPanels.personalityQuiz.emptyHint', {
          defaultValue: 'Додайте результати та питання у налаштуваннях праворуч',
        })}
      </div>
    );
  }

  const fontStyle = { fontFamily, color: textColor };

  if (result) {
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-center overflow-auto"
        style={{ padding: pad, gap, ...fontStyle }}
      >
        <style>{`@keyframes pq-pop { 0%{opacity:0; transform:scale(.92)} 100%{opacity:1; transform:scale(1)} }`}</style>
        <div className="flex flex-col items-center" style={{ gap, animation: 'pq-pop .4s ease-out' }}>
          <div style={{ fontSize: fs * 0.8, opacity: 0.7 }}>
            {t('games.personalityQuiz.yourResult', { defaultValue: 'Твій результат:' })}
          </div>
          {result.imageUrl && (
            <img
              src={getMediaUrl(result.imageUrl)}
              alt=""
              draggable={false}
              className="object-contain rounded-xl"
              style={{ maxHeight: `${Math.round(180 * s)}px`, maxWidth: '80%' }}
            />
          )}
          <div className="font-bold leading-tight" style={{ fontSize: fs * 1.6, color: accentColor }}>
            {result.title}
          </div>
          {result.description && (
            <div className="leading-snug whitespace-pre-line" style={{ fontSize: fs * 0.9, maxWidth: '90%' }}>
              {result.description}
            </div>
          )}
        </div>
        {interactive && (
          <button
            type="button"
            onClick={restart}
            className="flex items-center gap-1.5 rounded-lg text-white font-medium shadow-sm transition hover:opacity-90"
            style={{
              backgroundColor: accentColor,
              fontSize: fs * 0.8,
              padding: `${Math.round(6 * s)}px ${Math.round(14 * s)}px`,
              marginTop: gap,
            }}
          >
            <RotateCcw size={Math.max(10, Math.round(14 * s))} />
            {t('games.personalityQuiz.restart', { defaultValue: 'Пройти ще раз' })}
          </button>
        )}
      </div>
    );
  }

  const q = playableQuestions[Math.min(step, playableQuestions.length - 1)];
  const options = (q.options || []).filter((o) => (o.text && o.text.trim()) || o.imageUrl);
  const hasImages = options.some((o) => o.imageUrl);
  const cols = hasImages ? Math.min(options.length, 4) : options.length > 3 ? 2 : 1;

  return (
    <div className="w-full h-full flex flex-col overflow-auto" style={{ padding: pad, gap, ...fontStyle }}>
      {title && (
        <div className="text-center font-bold leading-tight" style={{ fontSize: fs * 1.3, color: accentColor }}>
          {title}
        </div>
      )}

      {/* Progress */}
      <div className="flex items-center" style={{ gap: gap * 0.6 }}>
        <button
          type="button"
          onClick={goBack}
          disabled={!interactive || step === 0}
          className="shrink-0 rounded-full transition disabled:opacity-0"
          style={{ color: accentColor }}
          aria-label={t('games.personalityQuiz.back', { defaultValue: 'Назад' })}
        >
          <ChevronLeft size={Math.max(12, Math.round(20 * s))} />
        </button>
        <div className="flex-1 rounded-full overflow-hidden" style={{ height: Math.max(3, Math.round(6 * s)), backgroundColor: 'rgba(0,0,0,0.08)' }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${(step / playableQuestions.length) * 100}%`, backgroundColor: accentColor }}
          />
        </div>
        <span className="shrink-0 tabular-nums" style={{ fontSize: fs * 0.7, opacity: 0.6 }}>
          {step + 1} / {playableQuestions.length}
        </span>
      </div>

      <div key={q.id} className="flex-1 flex flex-col justify-center" style={{ gap, animation: 'pq-fade .3s ease-out' }}>
        <style>{`@keyframes pq-fade { 0%{opacity:0; transform:translateX(12px)} 100%{opacity:1; transform:none} }`}</style>
        <div className="text-center font-semibold leading-snug" style={{ fontSize: fs * 1.1 }}>
          {q.text}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
            gap,
          }}
        >
          {options.map((o) => {
            const picked = pickedId === o.id || (!pickedId && answers[q.id] === o.id);
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => choose(q, o)}
                disabled={!interactive}
                className="flex flex-col items-center justify-center rounded-xl text-center transition"
                style={{
                  backgroundColor: picked ? accentColor : optionBgColor,
                  color: picked ? '#ffffff' : textColor,
                  border: `2px solid ${picked ? accentColor : 'rgba(0,0,0,0.1)'}`,
                  padding: `${Math.round(10 * s)}px ${Math.round(12 * s)}px`,
                  gap: gap * 0.6,
                  fontSize: fs,
                  fontFamily,
                  cursor: interactive ? 'pointer' : 'default',
                  transform: pickedId === o.id ? 'scale(0.97)' : 'none',
                }}
              >
                {o.imageUrl && (
                  <img
                    src={getMediaUrl(o.imageUrl)}
                    alt=""
                    draggable={false}
                    className="w-full object-contain rounded-lg"
                    style={{ maxHeight: `${Math.round(110 * s)}px` }}
                  />
                )}
                {o.text && <span className="leading-tight break-words">{o.text}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PersonalityQuizElement;
